import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import Banner from 'components/Banner';
import CompatibilityAdvisoryPanel from 'components/CompatibilityAdvisoryPanel';
import LowGpaConcentrationWarning from 'components/LowGpaConcentrationWarning';
import MixOrderAdvisoryPanel from 'components/MixOrderAdvisoryPanel';
import SameAiWarningBanner from 'components/SameAiWarningBanner';

import useAdvisoryData from './hooks/useAdvisoryData';
import useComplianceModal from './hooks/useComplianceModal';
import useEnrichRecipeProducts from './hooks/useEnrichRecipeProducts';
import useFetchMixRecipe from './hooks/useFetchMixRecipe';
import useMixApplyPageCalcs from './hooks/useMixApplyPageCalcs';
import useRecipeState from './hooks/useRecipeState';

import useCalcPanelData from './components/MixApplyCalculationsPanel/hooks/useData';
import StateComplianceModal from './components/StateComplianceModal';
import { MIX_DETAIL_ROUTE_PREFIX, TEST_ID } from './constants';
import { MOCK_APPLICATION, MOCK_TANK_STATE, MOCK_WEATHER_READING } from './mockData';
import styles from './protoStyles.module.scss';
import type { FunctionComponent, Props, TankStateFixture, WeatherReadingFixture } from './types';

const CARRIER_OPTIONS = ['Water', 'UAN 28', 'UAN 32', 'Liquid Fert'] as const;
const WIND_DIRECTIONS = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'] as const;

const CATEGORY_CLASS: Record<string, string> = {
    adjuvant: styles.badgeAdjuvant,
    fungicide: styles.badgeFungicide,
    growth: styles.badgeGrowth,
    herbicide: styles.badgeHerbicide,
    insecticide: styles.badgeInsecticide,
    surfactant: styles.badgeSurfactant
};

const SIGNAL_CLASS: Record<string, string> = {
    caution: styles.signalCaution,
    danger: styles.signalDanger,
    warning: styles.signalWarning
};

const STATUS_PILL_CLASS: Record<string, string> = {
    error: styles.statusError,
    ok: styles.statusOk,
    warn: styles.statusWarn
};

const formatCompactNumber = (value: number): string =>
    Number.isFinite(value) ? value.toLocaleString('en-US', { maximumFractionDigits: 2 }) : '—';

const capitalize = (input: string): string =>
    input.length === 0 ? input : input.charAt(0).toUpperCase() + input.slice(1);

const MixApplyPage: FunctionComponent<Props> = ({
    overrideApplication,
    overrideMixRecipe,
    overrideTankState,
    overrideWeather,
    testId = TEST_ID
}) => {
    const { mixRecipeId } = useParams<{ mixRecipeId: string }>();
    const navigate = useNavigate();
    const application = overrideApplication ?? MOCK_APPLICATION;
    const [tankState, setTankState] = useState<TankStateFixture>(
        overrideTankState ?? MOCK_TANK_STATE
    );
    const [weather, setWeather] = useState<WeatherReadingFixture>(
        overrideWeather ?? MOCK_WEATHER_READING
    );
    const complianceModal = useComplianceModal();
    const fetched = useFetchMixRecipe(mixRecipeId);
    const baseMixRecipe = overrideMixRecipe !== undefined ? overrideMixRecipe : fetched.mixRecipe;
    const { enrichedMixRecipe } = useEnrichRecipeProducts({ mixRecipe: baseMixRecipe });
    const mixRecipe = enrichedMixRecipe ?? baseMixRecipe;
    const { handleRateChange, rateOverrides } = useRecipeState();
    const calcs = useMixApplyPageCalcs({
        mixRecipe,
        rateOverrides,
        tankState,
        totalAcres: application.totalAcres
    });
    const calcPanelData = useCalcPanelData({
        application,
        mixRecipe,
        weather
    });

    // Pass only the enriched recipe to the advisory hooks (not the fallback to
    // baseMixRecipe), so we never fire requests with empty ingredients +
    // null formulationCode that the backend 400s.
    const advisory = useAdvisoryData({
        carrierType: tankState.carrierType,
        mixRecipe: enrichedMixRecipe
    });

    // Surface any products that couldn't have their AI load computed (e.g.
    // liquid product missing density, ingredient missing percentage). These
    // contributions return null lbAiPerAcre and are silently excluded from
    // the per-CAS total — which means a season-cap exceedance driven by a
    // skipped product would NOT fire. This banner lets operators see the
    // data gap instead of trusting a silent "all clear".
    const aiLoadGapProducts = advisory.aiLoadRows
        .flatMap(row => row.contributions)
        .filter(contribution => contribution.warning !== null)
        .map(contribution => contribution.labelName);
    const uniqueGapProducts = Array.from(new Set(aiLoadGapProducts));
    const aiLoadGapMessage =
        uniqueGapProducts.length > 0
            ? `AI load could not be computed for ${uniqueGapProducts.length} product${uniqueGapProducts.length === 1 ? '' : 's'} (${uniqueGapProducts.join(', ')}). Season-cap checks may miss an exceedance for these products.`
            : null;

    const handleBack = () => {
        if (mixRecipeId) {
            navigate(`${MIX_DETAIL_ROUTE_PREFIX}/${mixRecipeId}`);
        } else {
            navigate('/chemicals');
        }
    };

    const productCount = mixRecipe?.products.length ?? 0;
    const recipeName = mixRecipe?.name ?? '';
    const subtitle = mixRecipe
        ? `${recipeName} · ${productCount} product${productCount === 1 ? '' : 's'}`
        : 'Select a mix to begin';

    const contextText = `${MOCK_APPLICATION.customerName ?? ''} · ${MOCK_APPLICATION.fieldName} · ${MOCK_APPLICATION.state} · ${capitalize(MOCK_APPLICATION.crop)} · ${MOCK_APPLICATION.totalAcres} ac · ${MOCK_APPLICATION.applicationMethod}`;

    const partialTankVisible =
        calcs.partialLastTankGal > 0 && calcs.partialLastTankGal < tankState.appTankGal;

    return (
        <div className={styles.mixApplyPage} data-testid={testId}>
            <div className={styles.breadcrumb}>
                <span className={styles.crumb} onClick={() => navigate('/chemicals')}>
                    Chemicals
                </span>

                <span className={styles.crumbSep}>/</span>

                <span className={styles.crumb} onClick={() => navigate('/chemicals')}>
                    Mixes
                </span>

                {recipeName && (
                    <>
                        <span className={styles.crumbSep}>/</span>

                        <span className={styles.crumb} onClick={handleBack}>
                            {recipeName}
                        </span>
                    </>
                )}

                <span className={styles.crumbSep}>/</span>

                <span>Mix &amp; Apply</span>
            </div>

            <div className={styles.contextBanner}>
                <svg
                    className={styles.contextBannerIcon}
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='2'
                    viewBox='0 0 24 24'>
                    <path d='M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z' />

                    <circle cx='12' cy='10' r='3' />
                </svg>

                <div className={styles.contextBannerGrow}>
                    <strong>{`Application ${MOCK_APPLICATION.id}`}</strong>

                    {` · ${contextText}`}
                </div>
            </div>

            <div className={styles.pageHeader}>
                <div className={styles.pageHeaderStack}>
                    <h1 className={styles.pageTitle}>Mix &amp; Apply</h1>

                    <div className={styles.pageSubtitle}>{subtitle}</div>
                </div>

                <div className={styles.pageHeaderActions}>
                    <button
                        className={`${styles.btn} ${styles.btnGhost}`}
                        onClick={handleBack}
                        type='button'>
                        ← Mix Detail
                    </button>

                    <button className={`${styles.btn} ${styles.btnPrimary}`} type='button'>
                        Complete Application
                    </button>
                </div>
            </div>

            <div className={styles.threePanel}>
                <section className={styles.panel}>
                    <div className={styles.panelHeader}>
                        <div className={styles.panelHeaderTitle}>Configuration</div>
                    </div>

                    <div className={styles.panelBody}>
                        <div className={styles.panelSection}>
                            <div className={styles.panelSectionTitle}>Application Context</div>

                            <div className={styles.smallerMuted}>
                                Driven by Application Details — read-only here.
                            </div>

                            <div className={styles.readonlyRow}>
                                <div className={styles.rLabel}>Field</div>

                                <div className={styles.rValue}>{MOCK_APPLICATION.fieldName}</div>
                            </div>

                            <div className={styles.readonlyRow}>
                                <div className={styles.rLabel}>Date</div>

                                <div className={styles.rValue}>
                                    {new Date(MOCK_APPLICATION.date).toLocaleDateString('en-US')}
                                </div>
                            </div>

                            <div className={styles.readonlyRow}>
                                <div className={styles.rLabel}>State</div>

                                <div className={styles.rValue}>{MOCK_APPLICATION.state}</div>
                            </div>

                            <div className={styles.readonlyRow}>
                                <div className={styles.rLabel}>Crop</div>

                                <div className={styles.rValue}>
                                    {capitalize(MOCK_APPLICATION.crop)}
                                </div>
                            </div>

                            <div className={styles.readonlyRow}>
                                <div className={styles.rLabel}>Acres</div>

                                <div className={styles.rValue}>{MOCK_APPLICATION.totalAcres}</div>
                            </div>

                            <div className={styles.readonlyRow}>
                                <div className={styles.rLabel}>Method</div>

                                <div className={styles.rValue}>
                                    {MOCK_APPLICATION.applicationMethod
                                        .replace(/_/g, ' ')
                                        .toLowerCase()
                                        .replace(/\b\w/g, c => c.toUpperCase())}
                                </div>
                            </div>

                            <div className={styles.readonlyRow}>
                                <div className={styles.rLabel}>Applicator</div>

                                <div className={styles.rValue}>
                                    {MOCK_APPLICATION.applicatorName}
                                </div>
                            </div>

                            <div className={styles.readonlyRow}>
                                <div className={styles.rLabel}>License</div>

                                <div className={styles.rValue}>
                                    {MOCK_APPLICATION.applicatorLicense}
                                </div>
                            </div>

                            <div className={styles.readonlyRow}>
                                <div className={styles.rLabel}>Type</div>

                                <div className={styles.rValue}>
                                    {capitalize(MOCK_APPLICATION.applicatorType.toLowerCase())}
                                </div>
                            </div>

                            <div className={styles.readonlyRow}>
                                <div className={styles.rLabel}>Target Pest</div>

                                <div className={styles.rValue}>{MOCK_APPLICATION.targetPest}</div>
                            </div>
                        </div>

                        <div className={styles.panelSection}>
                            <div className={styles.panelSectionTitle}>Tank &amp; Carrier</div>

                            <div className={styles.fieldRow}>
                                <div className={styles.field}>
                                    <label className={styles.fieldLabel}>Mix Tank (gal)</label>

                                    <input
                                        onChange={e =>
                                            setTankState({
                                                ...tankState,
                                                mixTankGal: Number(e.target.value)
                                            })
                                        }
                                        className={`${styles.input} ${styles.inputSm}`}
                                        min='0'
                                        step='1'
                                        type='number'
                                        value={tankState.mixTankGal}
                                    />
                                </div>

                                <div className={styles.field}>
                                    <label className={styles.fieldLabel}>App Tank (gal)</label>

                                    <input
                                        onChange={e =>
                                            setTankState({
                                                ...tankState,
                                                appTankGal: Number(e.target.value)
                                            })
                                        }
                                        className={`${styles.input} ${styles.inputSm}`}
                                        min='0'
                                        step='0.5'
                                        type='number'
                                        value={tankState.appTankGal}
                                    />
                                </div>
                            </div>

                            <div className={styles.fieldRow}>
                                <div className={styles.field}>
                                    <label className={styles.fieldLabel}>Carrier</label>

                                    <select
                                        onChange={e =>
                                            setTankState({
                                                ...tankState,
                                                carrierType: e.target
                                                    .value as (typeof CARRIER_OPTIONS)[number]
                                            })
                                        }
                                        className={`${styles.selectInput} ${styles.inputSm}`}
                                        value={tankState.carrierType}>
                                        {CARRIER_OPTIONS.map(opt => (
                                            <option key={opt} value={opt}>
                                                {opt}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className={styles.field}>
                                    <label className={styles.fieldLabel}>Rate gal/ac</label>

                                    <input
                                        onChange={e =>
                                            setTankState({
                                                ...tankState,
                                                carrierRateGalPerAc: Number(e.target.value)
                                            })
                                        }
                                        className={`${styles.input} ${styles.inputSm}`}
                                        min='0'
                                        step='0.1'
                                        type='number'
                                        value={tankState.carrierRateGalPerAc}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className={styles.panelSection}>
                            <div className={styles.panelSectionTitleRow}>
                                <span className={styles.panelSectionTitle}>Weather Conditions</span>

                                <button
                                    className={`${styles.btn} ${styles.btnSecondary}`}
                                    type='button'>
                                    Auto-fill
                                </button>
                            </div>

                            <div className={styles.fieldRow}>
                                <div className={styles.field}>
                                    <label className={styles.fieldLabel}>Wind mph</label>

                                    <input
                                        onChange={e =>
                                            setWeather({
                                                ...weather,
                                                windSpeedMph: Number(e.target.value)
                                            })
                                        }
                                        className={`${styles.input} ${styles.inputSm}`}
                                        step='0.1'
                                        type='number'
                                        value={weather.windSpeedMph}
                                    />
                                </div>

                                <div className={styles.field}>
                                    <label className={styles.fieldLabel}>Gust mph</label>

                                    <input
                                        onChange={e =>
                                            setWeather({
                                                ...weather,
                                                gustMph: Number(e.target.value)
                                            })
                                        }
                                        className={`${styles.input} ${styles.inputSm}`}
                                        step='0.1'
                                        type='number'
                                        value={weather.gustMph}
                                    />
                                </div>
                            </div>

                            <div className={styles.fieldRow}>
                                <div className={styles.field}>
                                    <label className={styles.fieldLabel}>Direction</label>

                                    <select
                                        onChange={e =>
                                            setWeather({
                                                ...weather,
                                                windDirection: e.target
                                                    .value as (typeof WIND_DIRECTIONS)[number]
                                            })
                                        }
                                        className={`${styles.selectInput} ${styles.inputSm}`}
                                        value={weather.windDirection}>
                                        {WIND_DIRECTIONS.map(d => (
                                            <option key={d} value={d}>
                                                {d}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className={styles.field}>
                                    <label className={styles.fieldLabel}>Temp °F</label>

                                    <input
                                        onChange={e =>
                                            setWeather({
                                                ...weather,
                                                temperatureF: Number(e.target.value)
                                            })
                                        }
                                        className={`${styles.input} ${styles.inputSm}`}
                                        type='number'
                                        value={weather.temperatureF}
                                    />
                                </div>
                            </div>

                            <div className={styles.field}>
                                <label className={styles.fieldLabel}>Humidity %</label>

                                <input
                                    onChange={e =>
                                        setWeather({
                                            ...weather,
                                            humidityPct: Number(e.target.value)
                                        })
                                    }
                                    className={`${styles.input} ${styles.inputSm}`}
                                    max='100'
                                    min='0'
                                    type='number'
                                    value={weather.humidityPct}
                                />
                            </div>

                            <div
                                className={`${styles.banner} ${styles.bannerInfo} ${styles.bannerSmaller}`}>
                                Weather thresholds are org defaults. Per-product thresholds require
                                Agrian Compliance tier (v1.x).
                            </div>
                        </div>
                    </div>
                </section>

                <section className={styles.panel}>
                    <div className={styles.panelHeader}>
                        <div className={styles.panelHeaderTitle}>Product Mix</div>

                        <span className={styles.panelHeaderMeta}>
                            From mix: <strong>{recipeName || '—'}</strong>
                        </span>
                    </div>

                    <div className={styles.panelBody}>
                        <div className={styles.panelSection}>
                            <div className={styles.panelSectionTitleRow}>
                                <span className={styles.panelSectionTitle}>
                                    Recipe · {productCount} Products
                                </span>

                                <button
                                    className={`${styles.btn} ${styles.btnGhost}`}
                                    onClick={handleBack}
                                    style={{ fontSize: '11px', padding: '6px 12px' }}
                                    type='button'>
                                    Edit Mix Definition
                                </button>
                            </div>

                            {(mixRecipe?.products ?? []).map((product, index) => {
                                const currentRate =
                                    rateOverrides[product.productId] ?? product.rate;
                                const rangeText = product.cropMatchRateRange
                                    ? `${product.cropMatchRateRange.minimum}–${product.cropMatchRateRange.maximum} ${product.rateUnit}`
                                    : product.labelRateRange
                                      ? `Label: ${product.labelRateRange.minimum}–${product.labelRateRange.maximum} ${product.rateUnit}`
                                      : '';
                                const isCropMatch = Boolean(product.cropMatchRateRange);

                                return (
                                    <div className={styles.productCard} key={product.productId}>
                                        <div className={styles.pcHeader}>
                                            <div className={styles.pcTitleRow}>
                                                <div className={styles.orderBadge}>{index + 1}</div>

                                                <div className={styles.pcTitleCol}>
                                                    <div className={styles.pcTitle}>
                                                        {product.productName}
                                                    </div>

                                                    <div className={styles.pcSub}>
                                                        EPA {product.epaRegNumber}

                                                        {product.formulation &&
                                                            ` · ${product.formulation}`}

                                                        {product.manufacturer &&
                                                            ` · ${product.manufacturer}`}
                                                    </div>

                                                    <div className={styles.pcBadges}>
                                                        {product.category && (
                                                            <span
                                                                className={`${styles.badge} ${CATEGORY_CLASS[product.category]}`}>
                                                                {capitalize(product.category)}
                                                            </span>
                                                        )}

                                                        {product.modeOfActionCode && (
                                                            <span
                                                                className={`${styles.badge} ${styles.badgeDefault}`}>
                                                                {product.modeOfActionCode}
                                                            </span>
                                                        )}

                                                        {product.signalWord && (
                                                            <span
                                                                className={`${styles.signalChip} ${SIGNAL_CLASS[product.signalWord]}`}>
                                                                {product.signalWord}
                                                            </span>
                                                        )}

                                                        {product.isDicamba && (
                                                            <span
                                                                className={`${styles.badge} ${styles.badgeSurfactant}`}>
                                                                Dicamba
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className={styles.pcRateRow}>
                                            <span className={styles.pcRateLabel}>Rate</span>

                                            <input
                                                onChange={e =>
                                                    handleRateChange({
                                                        productId: product.productId,
                                                        rate: Number(e.target.value)
                                                    })
                                                }
                                                className={styles.pcRateInput}
                                                min='0'
                                                step='0.1'
                                                type='number'
                                                value={currentRate}
                                            />

                                            <span
                                                className={`${styles.pcRateRange}${isCropMatch ? ` ${styles.pcRateRangeCropMatch}` : ''}`}>
                                                {rangeText}
                                            </span>

                                            <span className={styles.pcRateUnit}>
                                                {product.rateUnit}
                                            </span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </section>

                <section className={styles.panel}>
                    <div className={styles.panelHeader}>
                        <div className={styles.panelHeaderTitle}>Calculations &amp; Compliance</div>

                        <span
                            className={`${styles.statusPill} ${STATUS_PILL_CLASS[calcPanelData.statusLevel]}`}>
                            <span className='dot' />

                            {calcPanelData.statusText}
                        </span>
                    </div>

                    <div className={styles.panelBody}>
                        <div className={styles.statHero}>
                            <div className={styles.statHeroCol}>
                                <div className={styles.statHeroLabel}>Total Spray Volume</div>

                                <div>
                                    <span className={styles.statHeroValue}>
                                        {formatCompactNumber(calcs.totalSprayVolumeGal)}
                                    </span>

                                    <span className={styles.statHeroUnit}> gal</span>
                                </div>
                            </div>

                            <div
                                className={`${styles.statHeroCol}`}
                                style={{ alignItems: 'flex-end' }}>
                                <div className={styles.statHeroLabel}>Batches</div>

                                <div className={styles.statHeroValueSecondary}>{calcs.batches}</div>
                            </div>
                        </div>

                        <div className={styles.statsGrid}>
                            <div className={styles.statTile}>
                                <div className={styles.statLabel}>Fills / Batch</div>

                                <div className={styles.statValue}>
                                    {calcs.fillsPerBatch}

                                    <span className={styles.statUnit}>tanks</span>
                                </div>
                            </div>

                            <div className={styles.statTile}>
                                <div className={styles.statLabel}>Total App Fills</div>

                                <div className={styles.statValue}>
                                    {calcs.totalAppFills}

                                    <span className={styles.statUnit}>tanks</span>
                                </div>
                            </div>

                            <div className={styles.statTile}>
                                <div className={styles.statLabel}>Acres / Tank</div>

                                <div className={styles.statValue}>
                                    {formatCompactNumber(calcs.acresPerTank)}

                                    <span className={styles.statUnit}>ac</span>
                                </div>
                            </div>

                            <div className={styles.statTile}>
                                <div className={styles.statLabel}>Partial Last Tank</div>

                                <div className={styles.statValue}>
                                    {formatCompactNumber(calcs.partialLastTankGal)}

                                    <span className={styles.statUnit}>gal</span>
                                </div>
                            </div>

                            <div className={styles.statTile}>
                                <div className={styles.statLabel}>Water Volume</div>

                                <div className={styles.statValue}>
                                    {formatCompactNumber(calcs.waterVolumeGal)}

                                    <span className={styles.statUnit}>gal</span>
                                </div>
                            </div>

                            <div className={styles.statTile}>
                                <div className={styles.statLabel}>Product Volume</div>

                                <div className={styles.statValue}>
                                    {formatCompactNumber(calcs.productVolumeGal)}

                                    <span className={styles.statUnit}>gal</span>
                                </div>
                            </div>
                        </div>

                        {partialTankVisible && (
                            <div className={`${styles.banner} ${styles.bannerWarn}`}>
                                <span>
                                    Last tank fill will be partial at{' '}

                                    {formatCompactNumber(calcs.partialLastTankGal)} gal.
                                </span>
                            </div>
                        )}

                        {!calcs.carrierSufficient && (
                            <div className={`${styles.banner} ${styles.bannerWarn}`}>
                                <span>
                                    Product volume ({formatCompactNumber(calcs.productVolumeGal)}{' '}
                                    gal) exceeds total spray volume (

                                    {formatCompactNumber(calcs.totalSprayVolumeGal)} gal) — increase
                                    GPA or reduce load.
                                </span>
                            </div>
                        )}

                        <LowGpaConcentrationWarning
                            carrierRateGalPerAc={tankState.carrierRateGalPerAc}
                            productConcentrationPct={calcs.productConcentrationPct}
                        />

                        <SameAiWarningBanner rows={advisory.aiLoadRows} />

                        {aiLoadGapMessage && (
                            <Banner icon='warning' text={aiLoadGapMessage} variant='warn' />
                        )}

                        <div className={styles.panelSection}>
                            <MixOrderAdvisoryPanel
                                hasError={advisory.mixOrderError}
                                isLoading={advisory.isMixOrderLoading}
                                issues={advisory.mixOrderIssues}
                                orderedChemicals={advisory.mixOrderedChemicals}
                            />
                        </div>

                        <div className={styles.panelSection}>
                            <CompatibilityAdvisoryPanel
                                amsRecommendation={advisory.compatAmsRecommendation}
                                hasError={advisory.compatError}
                                interactions={advisory.compatInteractions}
                                isLoading={advisory.isCompatLoading}
                                isUsingDefaultWaterProfile
                                phWarnings={advisory.compatPhWarnings}
                            />
                        </div>

                        <div className={styles.panelSection}>
                            <div className={styles.panelSectionTitle}>Product Amounts to Load</div>

                            <table className={styles.amountsTable}>
                                <thead>
                                    <tr>
                                        <th>Product</th>

                                        <th>Per Tank</th>

                                        <th>Total</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {calcs.productAmounts.map(pa => (
                                        <tr key={pa.productId}>
                                            <td className={styles.amountsTablePname}>
                                                {pa.productName}
                                            </td>

                                            <td>{formatCompactNumber(pa.perMixTankOz)} oz</td>

                                            <td>{formatCompactNumber(pa.totalJobOz)} oz</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className={styles.panelSection}>
                            <div className={styles.panelSectionTitle}>Weather Validation</div>

                            <div className={styles.weatherRestrictions}>
                                {calcPanelData.weatherValidationCards.map(card => {
                                    const wrClass =
                                        card.status === 'ok'
                                            ? styles.wrProductOk
                                            : card.status === 'warn'
                                              ? styles.wrProductWarn
                                              : styles.wrProductError;

                                    return (
                                        <div
                                            className={`${styles.wrProduct} ${wrClass}`}
                                            key={card.title}>
                                            <div className={styles.wrName}>
                                                <span>{card.title}</span>

                                                <span
                                                    className={`${styles.statusPill} ${STATUS_PILL_CLASS[card.status]}`}>
                                                    <span className='dot' />

                                                    {card.statusLabel}
                                                </span>
                                            </div>

                                            <div className={styles.wrItems}>
                                                {card.items.map((item, i) => {
                                                    const itemClass =
                                                        item.pass === true
                                                            ? styles.wrItemPass
                                                            : item.pass === false
                                                              ? styles.wrItemFail
                                                              : '';

                                                    return (
                                                        <div
                                                            className={`${styles.wrItem} ${itemClass}`}
                                                            key={i}>
                                                            <span>{item.label}</span>

                                                            <span className={styles.wrValue}>
                                                                {item.value}
                                                            </span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        <div className={styles.panelSection}>
                            <div className={styles.panelSectionTitle}>
                                State Compliance · {MOCK_APPLICATION.state}
                            </div>

                            {calcPanelData.stateComplianceItems.map((item, i) => {
                                const rowClass =
                                    item.level === 'ok'
                                        ? styles.complianceRowOk
                                        : item.level === 'warn'
                                          ? styles.complianceRowWarn
                                          : styles.complianceRowBlock;

                                return (
                                    <div className={`${styles.complianceRow} ${rowClass}`} key={i}>
                                        <span>{item.text}</span>
                                    </div>
                                );
                            })}
                        </div>

                        <div className={styles.panelSection}>
                            <div className={`${styles.stack} ${styles.stackS8}`}>
                                <button
                                    className={`${styles.btn} ${styles.btnPrimary} ${styles.btnLg} ${styles.btnFull}`}
                                    onClick={complianceModal.open}
                                    type='button'>
                                    Generate State Compliance Report
                                </button>

                                <button
                                    className={`${styles.btn} ${styles.btnGhost} ${styles.btnFull}`}
                                    disabled
                                    type='button'>
                                    Grower Report{' '}

                                    <span className={styles.smallerMuted} style={{ marginLeft: 6 }}>
                                        coming soon
                                    </span>
                                </button>
                            </div>
                        </div>
                    </div>
                </section>
            </div>

            {mixRecipe && (
                <StateComplianceModal
                    application={MOCK_APPLICATION}
                    isOpen={complianceModal.isOpen}
                    mixRecipe={mixRecipe}
                    onClose={complianceModal.close}
                    tankState={tankState}
                    weather={weather}
                />
            )}
        </div>
    );
};

export default MixApplyPage;
